import React from "react";
import classNames from "classnames";

import { Link } from "gatsby";
import { useDispatch } from "react-redux";
import { triggerContact } from "../../redux/appReducer";

/**
 * @param {string} title  Card title
 * @param {string} description   Card description
 * @param {background} background   Card background
 * @param {string} href Card action link
 * @param {string} action   Card action text
 * @param {string} extended  Card var for full width
 *
 * @return  {component}
 */

const CardLink = (props) => {
  const { to, target, children } = props;
  if (to)
    return (
      <Link to={to} target={target}>
        {children}
      </Link>
    );
  return children;
};

const Card = (props) => {
  const {
    t1,
    t2,
    title,
    description,
    background,
    href,
    target,
    image,
    extended,
    extendedText,
    reversed,
    contrast,
    squared,
    action,
    aspect,
    contact,
  } = props;

  const dispatch = useDispatch();

  const handleContact = () => {
    if (contact) dispatch(triggerContact());
  };

  const handleClick = (e) => {
    e.stopPropagation();
    handleContact();
  };

  const className = classNames(
    {
      "aspect-[0.7]": background && true,
      "aspect-[0.5]": background && extended,
      "md:aspect-2": extended,
      "md:aspect-1": !extended,
      ...aspect,
    },
    { "col-span-1": true, "md:col-span-2": extended },
    "overflow-hidden",
    "cursor-pointer",
    "text-center",
    "rounded-3xl",
    "font-medium",
    "bg-white",
    "relative",
    "text-lg",
    "group",
    "md:p-10"
  );

  const classNameTitle = classNames(
    "md:text-[34px] leading-snug",
    "text-2xl",
    { "font-bold": true },
    /* { "font-bold": !contrast }, */
    /* { "font-medium": contrast }, */
    "pb-5",

    {
      "text-white": !contrast,
      "text-black": contrast,
    }
  );

  const classNameDescription = classNames("text-md md:text-lg", "font-normal", {
    "text-white": !contrast,
    "text-black": contrast,
  });

  const classNameBackground = classNames(
    "md:group-hover:scale-105 transition ease-in-out delay-150",
    "object-cover",
    "duration-500",
    "rounded-3xl",
    "ease-in-out",
    "absolute",
    "h-full",
    "w-full",
    "left-0",
    "top-0",
    { "top-[15%] md:top-0": squared, "left-[-25px] md:top-0": squared }
  );

  const classNameImage = classNames(
    "md:group-hover:scale-105 transition ease-in-out delay-150",
    "absolute md:relative",
    "relative",
    "bottom-0",
    "object-contain"
  );

  const classNameButton = classNames(
    "whitespace-nowrap rounded-xl md:px-4 md:py-2 font-medium inline-flex items-center",
    "mt-5",
    "p-2 md:px-2.5 -m-2 md:m-0",
    {
      "text-white": !contrast,
      "text-black": contrast,
    },
    "md:group-hover:text-white",
    "md:group-hover:bg-[#5395F8]",
    "text-md"
  );

  const classRow = classNames(
    "relative md:absolute top-0 left-0 w-full h-full grid",
    {
      "grid-cols-1 md:grid-cols-2": extended,
      "md:grid-cols-1": extendedText,
    }
  );

  const classNameColumn = classNames(
    "grid text-left content-start md:content-between",
    { "pb-[45px] p-[45px]": !reversed },
    {
      "pb-[45px] pl-[45px] pt-[45px] pr-[50px]": reversed,
    },
    { "content-end": t1 || t2 }
  );

  return (
    <div
      onClick={handleClick}
      className={className}
      //    data-aos="fade-up"
      //    data-aos-duration="1500"
    >
      <CardLink to={href} target={target}>
        {background && (
          <img
            loading="lazy"
            src={background}
            alt=""
            className={classNameBackground}
          />
        )}
        <div className={classRow}>
          {reversed && (
            <div className="hidden md:block">
              {image && (
                <img
                  loading="lazy"
                  src={image}
                  alt=""
                  className={classNameImage}
                />
              )}
            </div>
          )}
          <div className={classNameColumn}>
            {extended && <div></div>}
            <div>
              <div className={classNameTitle}>{title}</div>
              <div className={classNameDescription}>{description}</div>
            </div>
            <div>
              {action && (
                <div className={classNameButton}>
                  <span className="hidden md:block">{action}</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="w-5 h-5 ml-1"
                  >
                    <path
                      fillRule="evenodd"
                      d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              )}
            </div>
            {(t1 || t2) && (
              <div className="text-white">
                {t1 && <div className="flex text-xl font-bold">{t1}</div>}
                {t2 && <div>{t2}</div>}
              </div>
            )}
          </div>
          {image && !reversed && (
            <img loading="lazy" src={image} alt="" className={classNameImage} />
          )}
          {image && reversed && (
            <div className="block md:hidden">
              <img
                loading="lazy"
                src={image}
                alt=""
                className={classNameImage}
              />
            </div>
          )}
        </div>
      </CardLink>
    </div>
  );
};

export { Card };
